/**
 * Created by tdunham on 6/22/2017.
 */
import {HttpErrorResponse}              from "@angular/common/http";

import {isNullOrUndefined}              from "util";

/**
 * Abstract base class for all database queries.
 */
export abstract class Query {

	protected endpoint: string                  = '';
	protected endpoints: string[]               = [];
	protected page: number                      = 0;
	protected page_low_value: number            = 0;
	protected per_page: number                  = 100;
	protected per_page_low_value: number        = 1;
	protected per_page_high_value: number       = 150;
	protected query_params: KeyValuePair[]      = [];


	/**
	 * Initialize required values.
	 */
	protected constructor() {

		this.setEndpoints();    // Force the initialization of endpoints.
		this.setPageValues();   // Force the initialization of page values.

		// Once the page high and low values are initialized, set the defaults to them.
		this.per_page = this.per_page_high_value;
		this.page = this.page_low_value;

	}


	/**
	 * For a given HTTP error generated by the HttpClient,
	 * return a suitable error message.
	 *
	 * @param error  The HttpClient error return.
	 * @return {string}  The error as a string.
	 */
	static getQueryErrorMessage(error:any): string {

		if (error instanceof HttpErrorResponse && ! isNullOrUndefined(error.error.message)) {

			let estatus:string = error.status ? error.status.toString() : 'unknown';
			return error.error.message + ' (HTTP error code ' + estatus + ')';

		} else if (error instanceof HttpErrorResponse) {

			const err = error.message || JSON.stringify(error.error);
			return `${error.status} - ${error.statusText || ''} Details: ${err}`;

		} else {

			return error.message ? error.message : error.toString();

		}

	}


	/**
	 * Return true if the provided error object is due
	 * to a 401 - Unauthorized response.
	 *
	 * @param error        The error message to examine.
	 * @return {boolean} Whether it's an access error or not.
	 */
	static isUnauthorizedAccessError(error:any): boolean {

		return (error instanceof HttpErrorResponse) && (401 === error.status);

	}


	/**
	 * Add the specified key/value pair to the query
	 * parameters for this request.  The method will
	 * encode the incoming value for use in the URL.
	 *
	 * @param param  A KeyValuePair object that contains a query parameter.
	 */
	addKeyValuePairParameter( param: KeyValuePair ) {

		if (! isNullOrUndefined(param)) {
			param.value = encodeURIComponent(param.value);
			this.query_params.push(param);
		}

	}


	/**
	 * Add the specified key/value pair strings to the query
	 * parameters for this request.  The method will
	 * encode the incoming value for use in the URL.
	 *
	 * @param {string} key    A key for a query parameter.
	 * @param {string} value  A value for a query parameter.
	 */
	addParameter( key: string, value: string ):void {

		let kv: KeyValuePair = { key: key, value: value };
		this.addKeyValuePairParameter(kv);

	}


	/**
	 * Return the current page value.
	 *
	 * @returns {number} The current page value.
	 */
	getPage():number {

		return this.page;

	}


	/**
	 * Return the URL specified by the settings in this
	 * Query.
	 *
	 * @param site  The start string for the URL.
	 */
	abstract getUrl( site: string ): string;


	/**
	 * Look for matching keys in the KeyValuePair
	 * and remove them if found.
	 *
	 * @param {string} key  The key to remove.
	 */
	removeParameter( key: string ):void {

		let indexes:number[] = [];

		// If a matching parameter is found, remove it.
		for ( let x = 0; x < this.query_params.length; x++ ) {

			if ( key === this.query_params[x]['key'] ) {

				indexes.push( x );

			}

		}

		if ( 0 !== indexes.length ) {

			for ( let y = ( indexes.length - 1 ); y >= 0; y-- ) {

				this.query_params.splice(indexes[y], 1);

			}

		}

	}


	/**
	 * Set the endpoint for this request.  The endpoint entry must
	 * match one of the valid endpoint values set in setEndpoints().
	 *
	 * The argument value will be added to the endpoint string with
	 * a "/" between them.
	 *
	 * @param endpoint  A string matching one of the endpoint values.
	 * @param argument  A string argument to be added to the endpoint.
	 * @returns void
	 */
	setEndpoint( endpoint: string, argument: string = '' ): void {

		if (! isNullOrUndefined(endpoint) && -1 !== this.endpoints.indexOf(endpoint)) {
			let variable = (0 === argument.length) ? "" : argument;
			this.endpoint = endpoint + variable;
		}

	}


	/**
	 * Initialize the list of valid endpoints for this query.
	 * Any endpoint set in setEndpoint() will be checked against
	 * the "this.endpoints" string array initialized by this
	 * method.
	 *
	 * @returns void
	 */
	abstract setEndpoints(): void;


	/**
	 * Which page of output from a request is being displayed?
	 *
	 * @param page  The page being displayed.
	 * @returns void
	 */
	setPage( page: number ): void {

		this.page = page;
		this.page = (this.page < this.page_low_value) ? this.page_low_value : this.page;

	}


	/**
	 * Set the page_low_value, per_page_low_value,
	 * and per_page_high_value variables during
	 * initialization.
	 *
	 * @returns void
	 */
	abstract setPageValues() : void;


	/**
	 * The number of items to be displayed on a page of results.
	 *
	 * @param per_page  Number of items to display per page.
	 * @returns void
	 */
	setPerPage( per_page: number ): void {

		this.per_page = per_page;
		this.per_page = (this.per_page < this.per_page_low_value) ? this.per_page_low_value : this.per_page;
		this.per_page = (this.per_page > this.per_page_high_value) ? this.per_page_high_value : this.per_page;

	}

	/**
	 * Force number of items to be displayed on a page of results to ignored low/high values.
	 *
	 * @param per_page  Number of items to display per page.
	 * @returns void
	 */
	setPerPageExtended(per_page: number): void {
		this.per_page = per_page;
	}

}



// WEBPACK FOOTER //
// ./src/app/services/query/query.ts